import CloseIcon from "@mui/icons-material/Close";
import { Box, DialogTitle } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import type { ReactNode } from "react";
import { isValidElement, useState } from "react";

import { ModalActionContext } from "./modal-action-context";

type Props = {
  type?: "submit" | "button" | "reset";
  title?: ReactNode;
  disabled?: boolean;
  children?: ReactNode;
  formId?: string;
  confirmAction?: () => Promise<void> | void;
  cancelAction?: () => void;
  open?: boolean;
  button?: ReactNode;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  buttonStyles?: object;
  confirmText?: string;
  cancelText?: string;
  fullWidth?: boolean;
  color?: "inherit" | "primary" | "secondary" | "error" | "info" | "success" | "warning";
  variant?: "text" | "outlined" | "contained";
  canOpen?: boolean;
  display?: boolean;
  btnSize?: "small" | "medium" | "large";
  noAction?: boolean;
};

function CustomModal({
  type = "submit",
  title,
  disabled,
  children,
  formId,
  confirmAction,
  cancelAction,
  open: InitialOpen = false,
  button,
  size = "md",
  buttonStyles,
  confirmText = "Créer le compte",
  cancelText = "Annuler",
  fullWidth,
  color = "primary",
  variant = "contained",
  canOpen = true,
  display = true,
  btnSize = "medium",
  noAction = false,
}: Props) {
  const [open, setOpen] = useState(InitialOpen ?? false);

  const handleClose = () => {
    if (cancelAction) {
      cancelAction();
    } else {
      setOpen(false);
    }
  };

  const handleButtonClick = () => {
    if (disabled) {
      return;
    }

    if (canOpen) {
      setOpen((v) => !v);
    } else if (confirmAction) {
      void confirmAction();
    }
  };

  const handleConfirmAction = async () => {
    if (confirmAction) {
      await confirmAction();
      setOpen(false);
    }
  };

  const isButtonElement = isValidElement(button);

  return (
    <>
      {isButtonElement ? (
        <Box
          sx={{ display: display ? "flex" : "none", ...buttonStyles }}
          onClick={handleButtonClick}
        >
          {button}
        </Box>
      ) : (
        <Button
          sx={{ display: display ? "flex" : "none", ...buttonStyles }}
          size={btnSize}
          fullWidth={fullWidth}
          disabled={disabled}
          onClick={handleButtonClick}
          color={color}
          variant={variant}
        >
          {button}
        </Button>
      )}
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth={size}
      >
        <DialogTitle
          id="alert-dialog-title"
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}
        >
          <span>{title}</span>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              ml: 2,
              color: (theme) => theme.palette.grey[500],
              position: "absolute",
              right: 8,
              top: 8,
            }}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {/* eslint-disable-next-line react/no-context-provider */}
          <ModalActionContext.Provider value={{ handleConfirmAction, closeModal: handleClose }}>
            {children}
          </ModalActionContext.Provider>
        </DialogContent>
        {noAction ? null : (
          <DialogActions>
            <Box sx={{ gap: 2, padding: 1, width: "100%", display: "flex" }}>
              <Button
                form={formId}
                type="reset"
                color="warning"
                variant="outlined"
                fullWidth
                onClick={handleClose}
              >
                {cancelText}
              </Button>
              <Button
                form={formId}
                type={type}
                onClick={() => {
                  void handleConfirmAction();
                }}
                variant="contained"
                disableElevation
                color="success"
                fullWidth
              >
                {confirmText}
              </Button>
            </Box>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
}

export default CustomModal;
// eslint-disable-next-line react-refresh/only-export-components
export { useModalAction, useOptionalModalAction } from "./custom-modal.utils";
